/**
 * ProviderBadge — the practitioner's card as the Practice calendar draws it on hover (its `ProviderCard
 * layout="detail"`, "the clinic access badge"): a tinted band with the lanyard slot and the clinic's seal pressed into
 * it, the 72 photo with its availability dot overlapping the band, the name (card-title role) with the issue glyph and
 * the ✓ that puts the practitioner in or out of the filter, the specialty, "On shift · 08:00 - 17:00" or "Off today",
 * a grey two-figure panel ("Free today", the slots count that opens the slots popover; "Next"), the next slots that
 * each start a booking, the "Book" tiles (In clinic / Video / Home) in the muted category colours, and a perforated
 * foot with the clinic's name and "Care team".
 *
 * It takes the same props as the Signal DS `PACalendarProviderCard` it replaces in the calendar chips and wires every
 * action to the same handlers: the ✓ to `onToggleSelect`, the slots count to `renderSlotsPopover` (plain figure when
 * the popover is not enabled: no slots, or no practitioner, practice, organization or profile id), a next slot to
 * `onSelectSlot(slot, preferredProfile)`, a Book tile to `onSelectProfile(group, preferredProfile)`.
 * All of its look is in `showcase-bridge.css`, `/* calendar practitioner badge *\/`. Data the strip does not carry
 * (the Practice card's settings link on the name) is left out unless `onClick` is given.
 */
import { AlertCircle, AlertTriangle, CheckCircle2, Circle } from 'lucide-react';
import { Avatar, type PACalendarProviderCardProps } from '@scripsteam/scrips-signal-ds';
import { locationGlyph } from '@/scheduling/lib/appointment-glyphs';
import { useAuthStore } from '@/auth/auth-store';
import { useSchedulingStore } from '@/scheduling/store/scheduling-store';
import { usePractitionerPractices } from '@/scheduling/services/use-practitioner-practices';
import type { CreateAppointmentProfileRequest } from '@/types/scheduling-api';
import { ClinicStamp } from './clinic-stamp';

type Profile = CreateAppointmentProfileRequest;
export type ProviderBadgeProps = Omit<PACalendarProviderCardProps<Profile>, 'initials'> & {
  initials?: string;
  /** Overrides the active practice's name (else the calendar's selected practice). */
  practiceName?: string | null;
  /** The slot feed failed: no figures, "Couldn't load slots" and Retry instead (PM `provider-card.tsx`). */
  slotsState?: 'error';
  onRetrySlots?: () => void;
};

const FALLBACK_PRACTICE = 'Scrips clinic';
const HUE: Record<string, string> = { office: 'jade', virtual: 'sky', home: 'orange', block: 'purple' };
const LOCATION_LABEL: Record<string, string> = { office: 'In clinic', virtual: 'Video', home: 'Home', block: 'Block' };

/** The provider's preferred profile, else the first of any group: the profile the slots popover and a next slot use. */
function pickPreferredProfile(groups: ProviderBadgeProps['quickBookGroups']): Profile | null {
  const all = groups.flatMap((g) => g.profiles ?? []);
  return all.find((p) => p.isPreferred) ?? all[0] ?? null;
}

export function ProviderBadge({
  name,
  specialty,
  photo,
  issue,
  available,
  slotsToday,
  schedule,
  nextSlots,
  nextSlotResponses,
  quickBookGroups,
  isSelected = false,
  onToggleSelect,
  onSelectProfile,
  onClick,
  practitionerId,
  organizationId,
  onSelectSlot,
  renderSlotsPopover,
  practiceName,
  slotsState,
  onRetrySlots,
}: ProviderBadgeProps) {
  const slotsFailed = slotsState === 'error';
  // The active practice's name, resolved as the top bar's switcher does: the calendar's selected practice in the
  // user's practice list, else the auth store's practice.
  const user = useAuthStore((st) => st.user);
  const { data: practiceList } = usePractitionerPractices(user?.practitionerId ?? user?.id);
  const selectedId = useSchedulingStore((st) => st.selectedPracticeId) ?? user?.practiceId;
  const activePractice = practiceList?.find((p) => p.practiceSetupId === selectedId)?.practiceName ?? user?.practiceName;
  // "Scrips clinic" when no practice is known, as in Practice.
  const practice = (practiceName ?? activePractice)?.trim() || FALLBACK_PRACTICE;
  const preferred = pickPreferredProfile(quickBookGroups);
  const practiceId = preferred?.practiceId;
  const slotsPopoverOn = !slotsFailed && slotsToday > 0 && !!practitionerId && !!practiceId && !!organizationId && !!preferred?.id;

  const figure = <span className="cal-badge-figure" data-link={slotsPopoverOn && renderSlotsPopover ? '' : undefined}>{slotsFailed ? '—' : slotsToday}</span>;
  const slotsFigure =
    slotsPopoverOn && renderSlotsPopover && preferred && practitionerId && practiceId && organizationId
      ? renderSlotsPopover(figure, {
          practitionerId,
          practiceId,
          organizationId,
          profile: preferred,
          onSelectSlot: onSelectSlot ? (slot) => onSelectSlot(slot, preferred) : undefined,
        })
      : figure;

  return (
    <div data-slot="provider-badge" className="cal-badge">
      <div aria-hidden className="cal-badge-band">
        <span className="cal-badge-lanyard" />
        <ClinicStamp practice={practice} size={148} className="cal-badge-stamp" />
      </div>
      <div className="cal-badge-photo-row">
        <div className="cal-badge-photo">
          <Avatar scale="compact" tone="provider" name={name} src={photo} size={72} />
          <span aria-label={available ? 'Available' : 'Unavailable'} className="cal-badge-dot" data-on={available ? '' : undefined} />
        </div>
      </div>
      <div className="cal-badge-who">
        <div className="cal-badge-name-row">
          {onClick ? (
            <button type="button" className="cal-badge-name" data-link="" onClick={onClick} title="Open provider settings">
              {name}
            </button>
          ) : (
            <p className="cal-badge-name">{name}</p>
          )}
          {issue?.message ? (
            <span className="cal-badge-issue" data-type={issue.type === 'error' ? 'error' : 'warning'} title={issue.message} aria-label={issue.message}>
              {issue.type === 'error' ? <AlertCircle aria-hidden /> : <AlertTriangle aria-hidden />}
            </span>
          ) : null}
          {onToggleSelect ? (
            // A real button (Enter and Space for free, and the app's one pointer-cursor rule) acting as a checkbox.
            <button
              type="button"
              role="checkbox"
              aria-checked={isSelected}
              aria-label={isSelected ? 'Deselect provider' : 'Select provider'}
              className="cal-badge-check"
              onClick={onToggleSelect}
            >
              {isSelected ? <CheckCircle2 aria-hidden /> : <Circle aria-hidden />}
            </button>
          ) : null}
        </div>
        {specialty ? <p className="cal-badge-specialty">{specialty}</p> : null}
        <p className="cal-badge-shift" data-on={available ? '' : undefined}>
          <span aria-hidden className="cal-badge-shift-dot" />
          <span className="cal-badge-shift-label">{available ? 'On shift' : 'Off today'}</span>
          {schedule ? <span className="cal-badge-shift-hours">· {schedule}</span> : null}
        </p>
      </div>
      <div className="cal-badge-today">
        <div className="cal-badge-today-cell">
          <span className="cal-badge-label">Free today</span>
          {slotsFigure}
        </div>
        <div className="cal-badge-today-cell">
          <span className="cal-badge-label">Next</span>
          <span className="cal-badge-figure">{slotsFailed ? '—' : nextSlots[0] ?? '—'}</span>
        </div>
      </div>
      <div className="cal-badge-body">
        <section className="cal-badge-section">
          <h4 className="cal-badge-label">Next slots</h4>
          {slotsFailed ? (
            <div className="cal-badge-slots" data-slot="provider-badge-slots-error">
              <span className="cal-badge-empty">Couldn't load slots</span>
              {onRetrySlots ? (
                <button type="button" className="cal-badge-slot" onClick={onRetrySlots}>
                  Retry
                </button>
              ) : null}
            </div>
          ) : nextSlots.length === 0 ? (
            <span className="cal-badge-empty">No slots available today</span>
          ) : (
            <div className="cal-badge-slots">
              {nextSlots.map((label, i) => {
                const raw = nextSlotResponses?.[i];
                const bookable = !!raw && !!preferred && !!onSelectSlot && !!raw.start && !!raw.end;
                return bookable ? (
                  <button
                    key={i}
                    type="button"
                    className="cal-badge-slot"
                    title="Book this slot"
                    onClick={() => onSelectSlot({ id: '', start: raw.start!, end: raw.end!, comment: raw.comment }, preferred)}
                  >
                    {label}
                  </button>
                ) : (
                  <span key={i} className="cal-badge-slot" data-static="">
                    {label}
                  </span>
                );
              })}
            </div>
          )}
        </section>
        {quickBookGroups.length > 0 ? (
          <section className="cal-badge-section">
            <h4 className="cal-badge-label">Book</h4>
            <div className="cal-badge-book" data-cols={Math.min(3, quickBookGroups.length)}>
              {quickBookGroups.map((g, i) => {
                const profile = g.profiles.find((p) => p.isPreferred) ?? g.profiles[0];
                if (!profile) return null;
                const loc = (g.location || 'office').toLowerCase();
                const TileGlyph = locationGlyph(loc);
                return (
                  <button
                    key={`${g.location}-${i}`}
                    type="button"
                    className="cal-badge-tile"
                    data-hue={HUE[loc] ?? 'jade'}
                    onClick={() => onSelectProfile?.(g, profile)}
                  >
                    <TileGlyph className="size-4 cal-badge-tile-icon" aria-hidden />
                    <span>{LOCATION_LABEL[loc] ?? g.location}</span>
                  </button>
                );
              })}
            </div>
          </section>
        ) : null}
      </div>
      <div className="cal-badge-foot">
        <ClinicStamp practice="" size={18} className="cal-badge-foot-stamp" />
        <span className="cal-badge-foot-name">{practice}</span>
        <span className="cal-badge-foot-meta">Care team</span>
      </div>
    </div>
  );
}
